import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import {
	Button,
	Divider,
	Stack,
	TextField,
	Typography,
	Card,
	CardContent,
	CardHeader,
	CardActions,
	IconButton,
} from "@mui/material";
import {
	NameValueModel,
	SystemSettingSaveMessage,
} from "Infrastructure/Models/Messages/Messages";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { FormValue, areAllFieldsValid } from "Infrastructure/Utils/FormUtils";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useState } from "react";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { v4 as uuidv4 } from "uuid";
import ClearAllIcon from "@mui/icons-material/ClearAll";

type SystemSettingSaveFormData = {
	isValidationShown: boolean;
	name: FormValue<string>;
	value: FormValue<string>;
};

const getNewFormData = () => {
	return {
		isValidationShown: false,
		name: {
			value: "",
		},
		value: {
			value: "",
		},
	};
};

type Props = {
	deviceForm: DeviceForm;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onSave: (message: SystemSettingSaveMessage) => void;
	formData: SystemSettingSaveFormData;
	setFormData: (data: SystemSettingSaveFormData) => void;
	settingsArray: NameValueModel[];
	setSettingsArray: (settings: NameValueModel[]) => void;
};

const SystemSettingsActionSave: FunctionComponent<Props> = (props) => {
	const {
		deviceForm,
		hubConnection,
		closeDialog,
		onSave,
		formData,
		setFormData,
		settingsArray,
		setSettingsArray,
	} = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const handleTextFieldChange = (e: any) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: { value: value, isValid: isFieldValid(name, value) },
		});
	};

	const isFieldValid = (name: string, value: any): boolean => {
		switch (name) {
			case "name":
				return !!value && value?.length > 0;
			case "value":
				return !!value && value?.length > 0;
			default:
				return true;
		}
	};

	const isFormValid = () => areAllFieldsValid(formData);

	const handleSubmit = () => {
		if (isFormValid()) {
			const newSetting: NameValueModel = {
				name: formData.name.value,
				value: formData.value.value,
			};

			setSettingsArray([...settingsArray, newSetting]);
			setFormData(getNewFormData());
		} else {
			setFormData({
				...formData,
				isValidationShown: true,
			});
		}
	};

	const handleRemoveSetting = (index: number) => {
		setSettingsArray(settingsArray.filter((_, i) => i !== index));
	};

	const handleConfirmSelection = () => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		const systemSettingSaveMessage: SystemSettingSaveMessage = {
			filteredDeviceNames: deviceForm.devices
				.filter((x) => x.isChecked)
				.map((x) => x.name),
			settings: settingsArray,
			requestId: uuidv4(),
		};

		onSave(systemSettingSaveMessage);
		setSettingsArray([]);
	};

	const formCanBeOpened = () => {
		if (deviceForm.devices.filter((x) => x.isChecked).length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return false;
		}
		return true;
	};

	const getConfirmDialogContent = () => (
		<Stack spacing={0.5}>
			{settingsArray.map((setting) => (
				<ul key={`${setting.name}-${setting.value}`}>
					<li>
						<strong>{setting.name || 'N/A'}:</strong> {setting.value || 'N/A'}
					</li>
				</ul>
			))}
		</Stack>
	);

	const isFormDirty = () => {
		const initial = getNewFormData();
		return Object.keys(formData).some((key) => {
			const val = (formData as any)[key]?.value;
			const initVal = (initial as any)[key]?.value;
			if (Array.isArray(val) && Array.isArray(initVal)) {
				return val.join() !== initVal.join();
			}
			return val !== initVal;
		});
	};

	return (
		<Card
			variant="outlined"
			elevation={0}
			sx={{ mt: 1 }}
			style={{ boxShadow: "none" }}
		>
			<CardHeader
				title={
					<Typography variant="subtitle1">
						Aktualizace
					</Typography>
				}
				action={
					<IconButton
						edge="end"
						size="small"
						onClick={() => {
							setFormData(getNewFormData());
							setSettingsArray([]);
						}}
						disabled={!isFormDirty() && settingsArray.length === 0}
						sx={{ mr: 1 }}
					>
						<ClearAllIcon />
					</IconButton>
				}
			/>
			<Divider />
			<form onSubmit={handleSubmit} autoComplete="off">
				<CardContent sx={{ pb: 0 }}>
					<Stack spacing={2}>
						<Stack direction="row" spacing={1} alignItems="center">
							<TextField
								name="name"
								label="Název (Name)"
								variant="outlined"
								size="small"
								fullWidth
								value={formData.name.value}
								inputProps={{ maxLength: 100 }}
								onChange={handleTextFieldChange}
								error={formData.isValidationShown && !formData.name.isValid}
							/>
							<TextField
								name="value"
								label="Hodnota (Value)"
								variant="outlined"
								size="small"
								fullWidth
								value={formData.value.value}
								inputProps={{ maxLength: 255 }}
								onChange={handleTextFieldChange}
								error={formData.isValidationShown && !formData.value.isValid}
							/>
							<Button
								variant="text"
								color="primary"
								onClick={handleSubmit}
								disabled={!isFormValid()}
							>
								Přidat
							</Button>
						</Stack>

						{/* Current Settings */}
						{settingsArray.length > 0 && (
							<Stack spacing={0.3}>
								<Typography>Vybrané změny:</Typography>
								<Divider />
								{settingsArray.map((setting, index) => (
									<Stack
										key={`${setting.name}-${setting.value}`}
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography>
											<strong>{setting.name || 'N/A'}:</strong> {setting.value || 'N/A'}
										</Typography>
										<Button
											variant="text"
											color="error"
											size="small"
											onClick={() => handleRemoveSetting(index)}
										>
											Zrušit
										</Button>
									</Stack>
								))}
								<Divider />
							</Stack>
						)}
					</Stack>
				</CardContent>
				<CardActions sx={{ justifyContent: "flex-end" }}>
					<BlConfirmButton
						modalTitle={"Aktualizace system settings"}
						modalContent={getConfirmDialogContent()}
						onResult={(result) => {
							if (!!result) {
								handleConfirmSelection();
							}
						}}
						disabled={settingsArray.length === 0}
						onClick={() => formCanBeOpened()}
					>
						Potvrdit
					</BlConfirmButton>
				</CardActions>
			</form>
		</Card>
	);
};

export default SystemSettingsActionSave;
